import React, {useContext, useEffect, useMemo, useState} from 'react';
import {useTable} from 'react-table';
import moment from 'moment-timezone';
import semver from 'semver';
import 'bootstrap/dist/css/bootstrap.min.css';
import {Badge, Table, Button, Tooltip, OverlayTrigger} from 'react-bootstrap';
import {S3Context} from './contexts/S3Context';
import {authenticatedFetch} from "./authenticatedFetch";
import config from './config';
import {EcsContext} from "./contexts/EcsContext";

const fetchData = async (url) => {
    try {
        const response = await authenticatedFetch(url);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error fetching data:', error);
        return null;
    }
};

const abbreviateVersion = (version) => {
    if (version.length > 15) {
        return `${version.slice(0, 8)}...${version.slice(-6)}`;
    }
    return version;
};

const isSemver = (version) => {
    return semver.valid(semver.coerce(version)) !== null;
};

const S3Websites = () => {
    const [data, setData] = useState({});
    const [loading, setLoading] = useState(true);
    const [lastRefreshed, setLastRefreshed] = useState(moment());
    const [nextRefresh, setNextRefresh] = useState(moment().add(5, 'minutes'));
    const [selectedVersions, setSelectedVersions] = useState([]);
    const { ecsClusters, ecsServices } = useContext(EcsContext);
    const {s3Websites} = useContext(S3Context);

    const uiNames = ["app.monitapp.io", "admin.monitapp.io"];
    const envOrder = ['dev', 'uat', 'xbx', 'prd'];

    const fetchDataFromAllEnvironments = async () => {
        setLoading(true);

        const allData = {};

        for (const uiName of uiNames) {
            const websiteBuckets = s3Websites[uiName];
            if (!websiteBuckets) {
                console.error(`No bucket configuration found for UI: ${uiName}`);
                continue;
            }

            const fetchPromises = Object.entries(websiteBuckets).map(([env, bucket]) =>
                fetchData(config.apiUrl + `/s3/` + env + `/website/` + bucket).then(data => ({env, data}))
            );

            const uiData = await Promise.all(fetchPromises);
            const processedData = {};

            uiData.forEach(({env, data}) => {
                if (data) {
                    processedData[env] = data;
                }
            });

            allData[uiName] = processedData;
        }

        setData(allData);
        setLastRefreshed(moment());
        setNextRefresh(moment().add(5, 'minutes'));
        setLoading(false);
    };

    useEffect(() => {
        fetchDataFromAllEnvironments();
        const interval = setInterval(fetchDataFromAllEnvironments, 5 * 60 * 1000);
        return () => clearInterval(interval);
    }, []);

    const formatDate = (date) => {
        const localTime = moment(date).local();
        return `${localTime.format('YYYY-MM-DD HH:mm:ss')} ${localTime.format('z')}`;
    };

    const handleVersionSelect = (version, env, uiName) => {
        if (!isSemver(version)) return;

        setSelectedVersions(prev => {
            const existingIndex = prev.findIndex(v => v.version === version && v.env === env && v.uiName === uiName);
            if (existingIndex !== -1) {
                return prev.filter((_, index) => index !== existingIndex);
            } else if (prev.length === 2) {
                return [prev[1], {version, env, uiName}];
            } else {
                return [...prev, {version, env, uiName}];
            }
        });
    };

    const getCompareLink = () => {
        if (selectedVersions.length !== 2 || selectedVersions[0].uiName !== selectedVersions[1].uiName) {
            return null;
        }

        const uiName = selectedVersions[0].uiName;
        const serviceInfo = ecsServices[uiName];
        if (!serviceInfo || !serviceInfo.repo) return null;

        const baseUrl = `https://github.com/${serviceInfo.owner}/${serviceInfo.repo}`;
        const [earlierVersion, laterVersion] = selectedVersions.sort((a, b) =>
            semver.compare(semver.coerce(a.version), semver.coerce(b.version))
        );
        return {
            url: `${baseUrl}/compare/${earlierVersion.version}...${laterVersion.version}`,
            repoName: `${serviceInfo.owner}/${serviceInfo.repo}`
        };
    };

    const clearSelection = () => {
        setSelectedVersions([]);
    };

    const columns = useMemo(
        () => {
            const envColumns = envOrder.map(env => ({
                Header: env.toUpperCase(),
                accessor: env,
                Cell: ({value, row}) => {
                    if (!value) return <span>-</span>;

                    const isSelected = selectedVersions.some(v => v.version === value.version && v.env === env && v.uiName === row.original.uiName);
                    const isSelectable = isSemver(value.version);

                    return (
                        <OverlayTrigger
                            placement="top"
                            overlay={
                                <Tooltip>
                                    <strong>Version:</strong> {value.version}<br/>
                                    <strong>Deployed At:</strong> {formatDate(value.deployedAt)}<br/>
                                    {isSelectable && <strong>Ctrl+Click (Cmd+Click on Mac)
                                        to {isSelected ? 'deselect' : 'select'} for comparison</strong>}
                                </Tooltip>
                            }
                        >
                            <Badge
                                bg={isSelected ? 'info' : 'primary'}
                                style={{cursor: 'pointer', opacity: isSelectable ? 1 : 0.6}}
                                onClick={(e) => {
                                    if (e.ctrlKey || e.metaKey) {
                                        handleVersionSelect(value.version, env, row.original.uiName);
                                    }
                                }}
                            >
                                {abbreviateVersion(value.version)}
                            </Badge>
                        </OverlayTrigger>
                    );
                },
            }));

            return [
                {
                    Header: 'UI Name',
                    accessor: 'uiName',
                    Cell: ({value}) => {
                        const serviceInfo = ecsServices[value];
                        if (serviceInfo && serviceInfo.repo) {
                            const repoUrl = `https://github.com/${serviceInfo.owner}/${serviceInfo.repo}`;
                            return (
                                <div style={{whiteSpace: 'nowrap'}}>
                                    <a href={repoUrl} target="_blank" rel="noopener noreferrer">
                                        {value}
                                    </a>
                                </div>
                            );
                        }
                        return <div style={{whiteSpace: 'nowrap'}}>{value}</div>;
                    },
                },
                ...envColumns
            ];
        },
        [data, selectedVersions, ecsServices]
    );

    const tableData = useMemo(() =>
            uiNames.map(uiName => ({
                uiName,
                ...data[uiName]
            })),
        [data]
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
    } = useTable({columns, data: tableData});

    useEffect(() => {
        const timer = setInterval(() => {
            setLastRefreshed(moment(lastRefreshed));
            setNextRefresh(moment(nextRefresh));
        }, 1000);
        return () => clearInterval(timer);
    }, [lastRefreshed, nextRefresh]);

    return (
        <div className="container-fluid mt-1">
            <h3>websites</h3>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <Button variant="primary" onClick={fetchDataFromAllEnvironments}>Refresh</Button>
                {lastRefreshed && (
                    <div>
                        Last Refresh: {lastRefreshed.fromNow()} | Next: {nextRefresh.fromNow()}
                    </div>
                )}
            </div>
            {selectedVersions.length > 0 && (
                <div className="mb-3">
                    <h5>Selected Versions:</h5>
                    {selectedVersions.map((v, i) => (
                        <Badge key={i} bg="info" className="me-2">
                            {v.uiName} - {v.env}: {abbreviateVersion(v.version)}
                        </Badge>
                    ))}
                    {getCompareLink() && (
                        <div>
                            <Button
                                variant="link"
                                href={getCompareLink().url}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Compare on GitHub
                            </Button>
                            <span className="text-muted">({getCompareLink().repoName})</span>
                        </div>
                    )}
                    <Button variant="secondary" size="sm" onClick={clearSelection} className="mt-2">
                        Clear Selection
                    </Button>
                </div>
            )}
            {loading ? (
                <div className="spinner-grow text-secondary" role="status">
                    <span className="sr-only"></span>
                </div>
            ) : (
                <div className="table-responsive">
                    <Table striped bordered hover size="sm" {...getTableProps()}>
                        <thead>
                        {headerGroups.map(headerGroup => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map(column => (
                                    <th {...column.getHeaderProps()}>
                                        {column.render('Header')}
                                    </th>
                                ))}
                            </tr>
                        ))}
                        </thead>
                        <tbody {...getTableBodyProps()}>
                        {rows.map(row => {
                            prepareRow(row);
                            return (
                                <tr {...row.getRowProps()}>
                                    {row.cells.map(cell => (
                                        <td {...cell.getCellProps()}>
                                            {cell.render('Cell')}
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                        </tbody>
                    </Table>
                </div>
            )}
        </div>
    );
};

export default S3Websites;